const API_BASE = '';

export interface ApiError {
    detail: string;
}

export interface UserInfo {
    id: string;
    username: string;
}

export interface PasteListItem {
    id: string;
    mode: string;
    visibility: string;
    expires_at: string | null;
    view_count: number;
    created_at: string;
}

export async function apiFetch<T>(
    path: string,
    options: RequestInit = {}
): Promise<T> {
    const url = `${API_BASE}${path}`;

    const response = await fetch(url, {
        ...options,
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json',
            ...options.headers,
        },
    });

    if (!response.ok) {
        const error: ApiError = await response.json().catch(() => ({
            detail: response.statusText,
        }));
        throw new Error(error.detail);
    }

    return response.json() as Promise<T>;
}

async function apiNoContent(path: string, method: string): Promise<void> {
    const response = await fetch(`${API_BASE}${path}`, {
        method,
        credentials: 'same-origin',
    });
    if (!response.ok) {
        const error: ApiError = await response.json().catch(() => ({
            detail: response.statusText,
        }));
        throw new Error(error.detail);
    }
}

export async function createPaste(body: Record<string, unknown>): Promise<Record<string, unknown>> {
    return apiFetch('/api/paste', {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

export async function getPaste(id: string, password?: string): Promise<Record<string, unknown>> {
    const headers: Record<string, string> = {};
    if (password) {
        headers['X-Paste-Password'] = password;
    }
    return apiFetch(`/api/paste/${id}`, { headers });
}

export async function getRawPaste(id: string): Promise<string> {
    const response = await fetch(`${API_BASE}/api/paste/${id}/raw`);
    if (!response.ok) {
        throw new Error('Failed to fetch raw paste');
    }
    return response.text();
}

export async function register(username: string, password: string): Promise<UserInfo> {
    return apiFetch('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
    });
}

export async function login(username: string, password: string): Promise<UserInfo> {
    return apiFetch('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
    });
}

export async function logout(): Promise<void> {
    await apiFetch('/api/auth/logout', { method: 'POST' });
}

export async function getMe(): Promise<UserInfo> {
    return apiFetch('/api/auth/me');
}

export async function listMyPastes(): Promise<PasteListItem[]> {
    return apiFetch('/api/me/pastes');
}

export async function updatePaste(id: string, body: Record<string, unknown>): Promise<Record<string, unknown>> {
    return apiFetch(`/api/paste/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
    });
}

export async function deletePaste(id: string): Promise<void> {
    await apiNoContent(`/api/paste/${id}`, 'DELETE');
}

export async function createShare(id: string): Promise<{ share_url: string }> {
    return apiFetch(`/api/paste/${id}/share`, { method: 'POST' });
}

export async function revokeShare(id: string): Promise<void> {
    await apiNoContent(`/api/paste/${id}/share`, 'DELETE');
}

export async function uploadMedia(
    pasteId: string,
    file: File
): Promise<{ media_id: string; url: string }> {
    const form = new FormData();
    form.append('paste_id', pasteId);
    form.append('file', file);
    const response = await fetch(`${API_BASE}/api/media/upload`, {
        method: 'POST',
        credentials: 'same-origin',
        body: form,
    });
    if (!response.ok) {
        const error: ApiError = await response.json().catch(() => ({
            detail: response.statusText,
        }));
        const err = new Error(error.detail) as Error & { status?: number };
        err.status = response.status;
        throw err;
    }
    return response.json();
}
